import type { MemoizedSelector } from '@ngrx/store';
import { createSelectorFactory, defaultMemoize } from '@ngrx/store';

import type { EntityOperationAsyncPayload, EntityState } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  createSelectorDeepEqual,
  getEntityCollectionActions,
  memoizeSelector,
  pipeState,
} from '@core/ngrx-legacy';
import type {
  ReportComponentGridPropsDecorated,
  ReportComponentInfoProps,
  ReportDashboardComponent,
  ReportResultsDataParams,
  ReportComponentStaticProps,
  ReportCreateComponentActionPayload,
  ReportCreateManyComponentActionPayload,
  ReportComponentDataProps,
} from '@core/reports-types';
import {
  buildApiDataParams,
  buildApiDataParamsHash,
  getVisualizationGridConstraints,
} from '@core/reports-util';

import { getComponentDataProps } from '../helpers/get-component-data-props';
import { getComponentVisualizationProps } from '../helpers/get-component-visualization-props';
import { safeReportsStateSelector } from '../helpers/safe-selector';
import { ReportsMetricsSelectors } from '../metrics/metrics.store';

export const selectComponentId = (
  component: Partial<Pick<ReportDashboardComponent, 'id' | 'localId'>>,
) => (component.id || component.localId)!;

const { actions, selectors, reducer, namespace, initialState } =
  createEntityCollectionStore<ReportDashboardComponent>()('ReportsComponents', {
    baseSelector: (root): EntityState<ReportDashboardComponent> =>
      safeReportsStateSelector(root, namespace, initialState),
    idSelector: selectComponentId,
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const {
        createOneOptimistic,
        createOneOptimisticSuccess,
        createOneOptimisticFailed,
        updateOneOptimistic,
        updateOneOptimisticSuccess,
        removeOneOptimistic,
      } = getEntityCollectionActions(adapter, selectComponentId);

      const createOptimisticComponentSuccess = (
        state: EntityState<ReportDashboardComponent>,
        payload: EntityOperationAsyncPayload<ReportDashboardComponent>,
      ) => createOneOptimisticSuccess(state, payload);

      return {
        addMany: (state, { data }: { data: ReportDashboardComponent[] }) =>
          adapter.addMany(state, data),
        createComponent: (state, { data, correlationId }: ReportCreateComponentActionPayload) =>
          createOneOptimistic(state, {
            data: { ...data.component, localId: correlationId },
            correlationId: correlationId,
          }),
        duplicateComponent: (state, { data, correlationId }: ReportCreateComponentActionPayload) =>
          createOneOptimistic(state, {
            data: { ...data.component, localId: correlationId },
            correlationId: correlationId,
          }),
        createOneOptimisticSuccess: createOptimisticComponentSuccess,
        createOneOptimisticFailed: (
          state,
          payload: Parameters<typeof createOneOptimisticFailed>[1] & { dashboardId: number },
        ) => createOneOptimisticFailed(state, payload),
        duplicateComponentSuccess: createOptimisticComponentSuccess,
        duplicateComponentFailed: createOneOptimisticFailed,
        updateComponent: (
          state,
          payload: {
            data: { dashboardId: number; component: ReportDashboardComponent };
            correlationId: string;
          },
        ) =>
          updateOneOptimistic(state, {
            data: payload.data.component,
            correlationId: payload.correlationId,
          }),
        updateOneOptimisticSuccess: (
          state,
          payload: { data: ReportDashboardComponent; correlationId: string },
        ) => updateOneOptimisticSuccess(state, payload),
        updateGridPropsOptimistic: (
          state,
          {
            correlationId,
            data,
          }: {
            correlationId: string;
            data: {
              dashboardId: number;
              components: Array<Pick<ReportDashboardComponent, 'id' | 'grid'>>;
            };
          },
        ) =>
          pipeState(
            state,
            next =>
              adapter.optimisticUpdateSet(next, correlationId, {
                before: <any>data.components.map(c => state.entityMap[selectComponentId(c)]!),
                update: <any>data.components,
              }),
            next => adapter.updateMany(next, data.components),
          ),
        updateGridPropsOptimisticSuccess: (
          state,
          { correlationId }: { data?: ReportDashboardComponent[]; correlationId: string },
        ) => {
          const patched = Object.values(state.optimisticUpdate)
            .flatMap(({ update }) => update)
            .reduce(
              (acc, entry) => {
                acc[entry.localId!] = entry;
                return acc;
              },
              <Record<string, any>>{},
            );

          return pipeState(
            state,
            next => adapter.optimisticUpdateRemove(next, correlationId),
            next => adapter.updateMany(next, Object.values(patched)),
          );
        },
        updateGridPropsOptimisticFailed: (
          state,
          { error, correlationId }: { correlationId: string; error: any },
        ) => {
          const entities: {
            before: Array<Pick<ReportDashboardComponent, 'id' | 'localId' | 'grid'>>;
            update: Array<Pick<ReportDashboardComponent, 'id' | 'localId' | 'grid'>>;
          } = <any>state.optimisticUpdate[correlationId];

          return pipeState(
            state,
            next => adapter.optimisticUpdateRemove(next, correlationId),
            next =>
              adapter.setError(next, error, {
                entities: entities.before.map(selectComponentId),
                scope: null,
              }),
            next => adapter.updateMany(next, entities.before),
          );
        },
        removeComponent: (
          state,
          {
            data,
          }: {
            data: { dashboardId: number; component: ReportDashboardComponent };
          },
        ) =>
          removeOneOptimistic(state, {
            data: data.component.id,
          }),
        setBuilderComponent: (state, { data }: { data: ReportDashboardComponent }) =>
          // TODO - this is the only exception to id being a number
          // builder could probably be handled differently
          adapter.setOne(state, { ...data, id: <any>'builder' }),
        createMany: (state, payload: ReportCreateManyComponentActionPayload) => state,
      };
    },
  });

const byIds = memoizeSelector((componentIds: number[]) =>
  createSelectorFactory(defaultMemoize)(
    ...(componentIds || []).map(componentId => selectors.byId(componentId)),
    (...result: ReportDashboardComponent[]) => result.filter(r => r !== undefined),
  ),
);

const componentGridProps = (id: number | string) =>
  createSelectorDeepEqual(
    selectors.byId(id),
    (
      component,
    ):
      | (ReportComponentGridPropsDecorated & {
          localId: string;
          refComponentId: string | number;
        })
      | null =>
      component
        ? {
            ...getVisualizationGridConstraints(component, component.grid),
            localId: component.localId!,
            refComponentId: component.id!,
            x: component.grid.x,
            y: component.grid.y,
          }
        : null,
  );

const componentFilterProps = (componentId: number | string) =>
  createSelectorDeepEqual(
    selectors.byId(componentId),
    (component): Pick<ReportDashboardComponent, 'filters' | 'dateFilter'> | null =>
      component
        ? {
            filters: component.filters,
            dateFilter: component.dateFilter,
          }
        : null,
  );

const componentDataProps = (id: number | string) =>
  createSelectorDeepEqual(selectors.byId(id), getComponentDataProps);

const componentVisualizationProps = (id: number | string) =>
  createSelectorDeepEqual(selectors.byId(id), getComponentVisualizationProps);

const componentStaticProps = (id: number | string) =>
  createSelectorDeepEqual(selectors.byId(id), (component): ReportComponentStaticProps | null =>
    component
      ? {
          id: component.id,
          localId: component.localId,
          isAvailable: component.isAvailable,
          unavailableReason: component.unavailableReason,
          origin: component.origin,
          templateId: component.templateId,
          generated: component.generated,
        }
      : null,
  );

const componentInfoProps = (id: number | string) =>
  createSelectorDeepEqual(selectors.byId(id), (component): ReportComponentInfoProps | null =>
    component ? { name: component.name, description: component.description } : null,
  );

const componentMetric = (id: number | string) =>
  createSelectorDeepEqual(
    selectors.byId(id),
    ReportsMetricsSelectors.entityMap,
    (component, metrics) => (component && metrics ? metrics[component.metric] : null),
  );

const componentDataPropsHash = (id: number | string) =>
  createSelectorDeepEqual(
    componentDataProps(id),
    componentMetric(id),
    (props, metric): string | null =>
      // TODO shared-analytics remove
      props && metric ? buildApiDataParamsHash(buildApiDataParams(props, metric)) : null,
  );

const componentMetricSlug = (id: number | string) =>
  createSelectorDeepEqual(selectors.byId(id), component => (component ? component.metric : null));

export type ComponentDataPropsSelector = MemoizedSelector<any, ReportComponentDataProps | null>;

/**
 *
 * @param dataPropsSelector - MemoizedSelector that returns ReportComponentDataProps
 * @returns Based on received selector, will build and return full api data params, including params, metric kind and hash of the params
 */
export const selectComponentDataParamsFactory =
  (dataPropsSelector: ComponentDataPropsSelector) => (id?: number | string) =>
    createSelectorDeepEqual(
      dataPropsSelector,
      ReportsMetricsSelectors.entityMap,
      (props, metrics): ReportResultsDataParams | null => {
        if (!props || !metrics || !(props.metric in metrics)) {
          return null;
        }
        const metric = metrics[props.metric]!;

        return buildApiDataParams(props, metric);
      },
    );

const componentDataParams = (id: string | number) =>
  selectComponentDataParamsFactory(componentDataProps(id))(id);

export const ReportsComponentsSelectors = {
  ...selectors,
  byIds,
  componentMetric,
  componentStaticProps,
  componentGridProps,
  componentDataProps,
  componentVisualizationProps,
  componentInfoProps,
  componentDataPropsHash,
  componentFilterProps,
  componentMetricSlug,
  componentDataParams,
};

export {
  actions as ReportsComponentsActions,
  reducer as ReportsComponentsReducer,
  namespace as ReportsComponentsNamespace,
};
